import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Server, Boxes, Building2, Share2, ScrollText, Users2,
  ShieldCheck, LogOut, CircleDot, Layers, ShieldAlert, Crosshair, Target, BadgeCheck, Gauge, GitBranch, Network, Radar, Link2, BookCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, perm: "dashboard:read", end: true },
  { to: "/assets", label: "Actifs", icon: Server, perm: "asset:read" },
  { to: "/services", label: "Services critiques", icon: Boxes, perm: "service:read" },
  { to: "/dependencies", label: "Cartographie", icon: Share2, perm: "dependency:read" },
  { to: "/supply-chain", label: "Supply chain", icon: Link2, perm: "supply_chain:read" },
  { to: "/failure-domains", label: "Domaines de défaillance", icon: Layers, perm: "asset:read" },
  { to: "/vulnerabilities", label: "Vulnérabilités", icon: ShieldAlert, perm: "vulnerability:read" },
  { to: "/threats", label: "Menaces", icon: Crosshair, perm: "threat:read" },
  { to: "/mitre", label: "MITRE ATT&CK", icon: Target, perm: "threat:read" },
  { to: "/controls", label: "Contrôles", icon: BadgeCheck, perm: "control:read" },
  { to: "/compliance", label: "Conformité", icon: BookCheck, perm: "control:read" },
  { to: "/risks", label: "Risques", icon: Gauge, perm: "risk:read" },
  { to: "/scenarios", label: "Scénarios", icon: GitBranch, perm: "scenario:read" },
  { to: "/twin", label: "Jumeau numérique", icon: Radar, perm: "twin:read" },
  { to: "/tenants", label: "Gestion des tenants", icon: Network, perm: "tenant:read" },
  { to: "/organizations", label: "Mon organisation", icon: Building2, perm: "org:read" },
  { to: "/audit", label: "Journal d'audit", icon: ScrollText, perm: "audit:read" },
  { to: "/users", label: "Utilisateurs", icon: Users2, perm: "user:read" },
];

export default function AppShell({ children, title, subtitle, actions }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className="w-64 shrink-0 border-r border-border bg-[hsl(240_10%_2.5%)] flex flex-col fixed h-screen"
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border">
          <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" aria-hidden="true" />
          </div>
          <div className="leading-tight">
            <div className="font-head font-extrabold tracking-tight text-lg">CROS</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
              v0.2
            </div>
            <div className="text-[10px] font-mono tracking-[0.1em] text-muted-foreground truncate max-w-[9.5rem]"
                 data-testid="sidebar-tenant" title={user?.org_name || "Tenant courant"}>
              Tenant : {user?.org_name || "Tenant courant"}
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto cros-scroll">
          {NAV.filter((n) => can(n.perm)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "bg-secondary text-white border-primary"
                    : "text-muted-foreground border-transparent hover:bg-secondary/60 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" aria-hidden="true" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-sm bg-secondary flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">
                {user?.name}
              </div>
              <div className="text-xs text-muted-foreground truncate">{user?.role_label}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2 rounded-sm"
            onClick={doLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </Button>
          {user?.is_demo && (
            <p className="mt-3 text-[10px] leading-snug text-muted-foreground/80 font-mono" data-testid="synthetic-data-notice">
              Environnement de démonstration — données synthétiques
            </p>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-8">
          <div>
            <h1 className="font-head text-xl font-bold tracking-tight leading-none">{title}</h1>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            <span
              className="flex items-center gap-1.5 text-xs text-foreground font-medium"
              title="Organisation active (tenant courant)"
              data-testid="active-org-name"
            >
              <CircleDot className="h-3 w-3 text-green-500" /> {user?.org_name || "—"}
            </span>
            {user?.is_demo && (
              <span
                className="px-2 py-0.5 rounded-sm border border-orange-500/30 bg-orange-500/10 text-orange-400 text-[10px] font-medium uppercase tracking-wider"
                data-testid="demo-badge"
              >
                Démonstration
              </span>
            )}
            {actions}
          </div>
        </header>
        <main className="flex-1 p-8 overflow-y-auto cros-scroll">{children}</main>
      </div>
    </div>
  );
}
